import { StoreService } from '../../../service/store/store.service';
import { CartPopupService } from '../../../service/cart/cart-popup.service';
import { paletteSku } from '../../../data/product/product-lookup.data';
import { Component, DoCheck, OnDestroy, OnInit } from '@angular/core';
import { animate, style, transition, trigger } from '@angular/animations';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { URLS } from '../../../data/navigation/navigation.data';
import { THEME_PALETTES } from '../../../data/palette/palette.data';
import { PALETTE_PLATFORM_VERSIONS } from '../../../data/product/platform-version.data';
import { THEMES } from '../../../data/product/product.data';
import { PaletteColor, PalettePlatform, ThemePalette } from '../../../model/palette/palette.model';
import { Product, isThemeFree } from '../../../model/product/product.model';
import { AnalyticsService } from '../../../service/analytics/analytics.service';
import { RoutingService } from '../../../service/routing/routing.service';
import { ThemeService } from '../../../service/theme/theme.service';
import { UtilService } from '../../../service/util/util.service';

// The palette detail page (/color-palette?product=<key>): the theme page's
// hero, one to one - the shared <app-banner> with the theme's wallpaper,
// logo, colors and tags - but the platforms column lists where the PALETTE
// ships (MacOS Color Palette selected, Adobe joins later) and the screenshot
// frame shows the palette inside the real MacOS color picker. Below, the
// palette showcase: all of its named colors as square tiles linking to their
// /tools/color pages. Premium themes carry their palette too - no platforms
// or screenshots for them yet, so the banner simply shows the theme and the
// tiles carry the page.
@Component( {
  selector: 'app-color-palette-page',
  templateUrl: './color-palette-page.component.html',
  styleUrls: [ './color-palette-page.component.scss' ],
  animations: [
    trigger( 'fadeEffect', [
      transition( ':enter', [
        style( { opacity: 0 } ),
        animate( '1s ease-in-out', style( { opacity: 1 } ) )
      ] )
    ] ),
    trigger( 'slideEffect', [
      transition( '* => *', [
        style( { transform: 'translateX(40%)', opacity: 0 } ),
        animate( '550ms cubic-bezier(0.22, 1, 0.36, 1)',
          style( { transform: 'translateX(0)', opacity: 1 } ) )
      ] )
    ] ),
    trigger( 'logoSlide', [
      transition( ':enter', [
        style( { transform: 'translateX(115%)', opacity: 0 } ),
        animate( '550ms cubic-bezier(0.22, 1, 0.36, 1)',
          style( { transform: 'translateX(0)', opacity: 1 } ) )
      ] ),
      transition( ':leave', [
        animate( '550ms cubic-bezier(0.22, 1, 0.36, 1)',
          style( { transform: 'translateX(-115%)', opacity: 0 } ) )
      ] )
    ] )
  ],
} )
export class ColorPalettePageComponent implements DoCheck, OnInit, OnDestroy {

  URLS = URLS;

  product: Product = undefined;
  palette: ThemePalette | null = null;
  category;

  selectedPlatformIndex = 0;

  darkModeActive = false;

  // The screenshot the banner is actually rendering (preloaded before the
  // slide animation plays - same mechanics as the theme page).
  displayedScreenshot: any = null;
  private lastTargetScreenshotSrc = '';

  private themeSub: Subscription;
  private routeSub: Subscription;

  readonly isThemeFree = isThemeFree;

  constructor(
    private route: ActivatedRoute,
    public router: Router,
    public utilService: UtilService,
    private routingService: RoutingService,
    private analyticsService: AnalyticsService,
    private themeService: ThemeService,
    private storeService: StoreService,
    private cartPopupService: CartPopupService ) {
  }

  ngOnInit(): void {
    this.themeSub = this.themeService.darkLightSetting.subscribe( () => {
      this.darkModeActive = this.themeService.selectDarkLightByCSS( false, true );
    } );

    this.routeSub = this.route.queryParams.subscribe( params => {
      if ( !params.product ) {
        this.router.navigate( [ URLS.notFound ] );
        return;
      }

      this.product = undefined;
      this.palette = null;
      setTimeout( () => {
        document.getElementById( 'head' )?.scrollIntoView( { behavior: 'smooth', block: 'start' } );
      }, 100 );

      const requestedKey = String( params.product ).toLowerCase();
      const themes = THEMES.map( line => line.products ).reduce( ( acc, val ) => acc.concat( val ), [] );
      this.product = themes.find( product => product.key.toLowerCase() === requestedKey );
      this.palette = THEME_PALETTES.find( palette => palette.key.toLowerCase() === requestedKey ) ?? null;

      if ( !this.product || !this.palette ) {
        this.router.navigate( [ URLS.notFound ] );
        return;
      }

      // ?platform= (the palettes' coverage table) opens on that app
      const requestedApp = String( params.platform || '' ).toLowerCase();
      this.selectedPlatformIndex = Math.max( 0, this.palette.platforms
        .findIndex( pl => pl.name.toLowerCase() === requestedApp || pl.key === requestedApp ) );

      if ( params.category ) {
        this.category = params.category;
      } else {
        const line = THEMES.find( l => l.products.some( p => p.key === this.product.key ) );
        this.category = line ? line.name : '';
      }
    } );
  }

  ngOnDestroy(): void {
    this.themeSub?.unsubscribe();
    this.routeSub?.unsubscribe();
  }

  get platforms(): PalettePlatform[] {
    return this.palette?.platforms ?? [];
  }

  get selectedPlatform(): PalettePlatform | null {
    return this.platforms[ this.selectedPlatformIndex ] ?? null;
  }

  /** The palette download's version, under the platform name in the pill
   *  (platform-version.data.ts, the same line the theme pages carry). */
  get selectedVersion(): string {
    const platform = this.selectedPlatform?.key;
    const key = this.palette?.key;
    if ( !platform || !key ) return '';
    return ( PALETTE_PLATFORM_VERSIONS[ key ] || {} )[ platform ] || '';
  }

  get selectedScreenshot(): any {
    return this.selectedPlatform?.screenshot ?? null;
  }

  selectPlatform( idx: number ): void {
    if ( idx !== this.selectedPlatformIndex ) {
      this.analyticsService.logEvent( 'Palette Platform Selected', {
        palette: this.product?.name,
        key: this.product?.key,
        platform: this.platforms[ idx ]?.key,
        source: 'palette-page',
      } );
    }
    this.selectedPlatformIndex = idx;
  }

  trackByPlatformName( _index: number, plat: { name: string } ): string {
    return plat.name;
  }

  trackByHex( _index: number, color: PaletteColor ): string {
    return color.hex;
  }

  /** Add to cart (owner 2026-10-12: a free palette goes through the cart like a theme) */
  getNow( platform: PalettePlatform ): void {
    const sku = this.skuOf( platform );
    if ( !this.storeService.canAdd( sku ) ) {
      return;
    }
    this.cartPopupService.add( sku, 'palette-page' );
  }

  /** the palette's store sku for an app ('aurora-borealis/figma-palette') */
  skuOf( platform: PalettePlatform | null | undefined ): string {
    return this.product && platform ? paletteSku( this.product.key, platform.key ) : '';
  }

  get cartDisabled(): boolean {
    return this.comingSoon || !this.storeService.canAdd( this.skuOf( this.selectedPlatform ) );
  }

  /** The 10-color marketing palette for the analysis sections below. */
  get marketingHexes(): string[] {
    return this.product?.palette ?? [];
  }

  /** Premium theme: its palettes are paid ($0.99 per app, 2026-10-15). */
  get premium(): boolean {
    return !!this.product && !isThemeFree( this.product );
  }

  /** a premium palette the store does not price yet (no screenshot, no Stripe product): Coming Soon */
  get comingSoon(): boolean {
    return this.premium && this.storeService.availability( this.skuOf( this.selectedPlatform ) ) !== 'buy';
  }

  get relatedTheme(): { product: Product, category: string } | null {
    return this.product ? { product: this.product, category: this.category || '' } : null;
  }

  goToTheme(): void {
    this.routingService.redirect( URLS.theme, {
      product: this.product.key,
      category: ( this.category || '' ).toLowerCase(),
    } );
  }

  /** Open the themes gallery pre-filtered to the clicked tag. */
  goToThemesByTag( tag: string ) {
    if ( !tag ) {
      return;
    }
    this.routingService.redirect( URLS.themes, { tag: tag.toLowerCase() } );
  }

  openRouteInNewTab( route: string ) {
    const url = this.router.serializeUrl( this.router.createUrlTree( [ route ] ) );
    window.open( url, '_blank' );
  }

  setScreenshotLoaded = ( screenshot: any ) => {
    screenshot.loaded = true;
  };

  /** Preload the target screenshot before promoting it (theme-page mechanics). */
  ngDoCheck(): void {
    const target = this.selectedScreenshot;
    const src = target?.src ?? '';
    if ( src === this.lastTargetScreenshotSrc ) {
      return;
    }
    this.lastTargetScreenshotSrc = src;
    this.preloadAndCommitScreenshot( target );
  }

  private preloadAndCommitScreenshot( target: any ): void {
    if ( !target?.src ) {
      this.displayedScreenshot = target;
      return;
    }
    const src = target.src;
    const img = new window.Image();
    const commit = () => {
      if ( this.lastTargetScreenshotSrc !== src ) {
        return;
      }
      this.displayedScreenshot = target;
      target.loaded = true;
    };
    img.onload = commit;
    img.onerror = commit;
    img.src = src;
    if ( img.complete && img.naturalHeight > 0 ) {
      commit();
    }
  }

  get displayedSlideKey(): string {
    return this.displayedScreenshot?.src ?? '';
  }
}
